<template>
  <div id="LocationMap" class="container">
    <section class="no-padding">
      <!-- Hide Pink Next Btn: when component was called as a pop up or form -->
      <div v-if="isForm" class="columns">
        <!-- Address Search Field -->
        <div class="column is-8">
          <div class="field">
            <div class="control is-clearfix">
              <geo-search
                @search_error="updateSearchError"
                @search_result="updateSearchLocationPin"
                :isSearch="geoSearchPressed"
              />
            </div>
          </div>
        </div>
        <!-- Buttons: Search + My Location -->
        <div class="column is-4">
          <div class="columns is-mobile is-variable is-2-mobile">
            <div class="column is-6-desktop is-11-mobile">
              <b-button
                class="full-width dtac-font"
                type="is-primary"
                @click.prevent="searchPlaceFromInput"
                >{{ $t("register.search.search_btn") }}</b-button
              >
            </div>
            <div class="column is-6-desktop is-1-mobile">
              <b-button
                outlined
                icon-right="crosshairs-gps"
                :type="myLocation ? 'is-primary' : 'is-dark'"
                :disabled="myLocation ? false : true"
                @click.prevent="myLocationButtonPress"
              />
            </div>
          </div>
        </div>
      </div>
      <!-- Show Pink Next Btn: when component was normally called -->
      <div v-else class="columns">
        <!-- Address Search Field -->
        <div class="column is-5">
          <div class="field">
            <div class="control is-clearfix">
              <geo-search
                @search_error="updateSearchError"
                @search_result="updateSearchLocationPin"
                :isSearch="geoSearchPressed"
              />
            </div>
          </div>
        </div>
        <!-- Buttons: Search + My Location + Planning -->
        <div class="column is-4">
          <div class="columns is-mobile is-variable is-2-mobile">
            <div
              class="column is-4-desktop"
              :class="
                userProfile.roles.map_area ? 'is-8-mobile' : 'is-10-mobile'
              "
            >
              <b-button
                class="full-width dtac-font"
                type="is-primary"
                @click.prevent="searchPlaceFromInput"
                >{{ $t("register.search.search_btn") }}</b-button
              >
            </div>
            <!-- Enable My Location -->
            <div class="column is-2-desktop is-2-mobile">
              <b-button
                outlined
                icon-right="crosshairs-gps"
                :type="myLocation ? 'is-primary' : 'is-dark'"
                :disabled="myLocation ? false : true"
                @click="myLocationButtonPress"
              />
            </div>
            <!-- Enable Planning Area -->
            <div
              v-if="userProfile.roles.map_area"
              class="column is-2-desktop is-2-mobile"
            >
              <b-button
                outlined
                type="is-primary"
                icon-right="radar"
                @click="showAreaButtonPress"
              />
            </div>
          </div>
        </div>
        <!-- Offset is-1 -->
        <div class="column is-1 is-hidden-mobile" />
        <!-- Button: Next ( to register form ) -->
        <div v-if="isServiceAvailable" class="column is-2">
          <b-button
            type="is-pink"
            class="full-width dtac-font"
            @click.prevent="goToNumberRegisterStep"
            >{{ $t("register.search.next_btn") }}</b-button
          >
        </div>
      </div>

      <!-- Service Location Maps -->
      <gmap-map
        ref="locationmap"
        class="map"
        :center="center"
        :zoom="12"
        @idle="mapIdleObserver"
        @click="observerGetPinCoordinates"
      >
        <!-- Marker: My Location -->
        <gmap-marker
          :position="myLocation"
          :icon="{ url: require('../../assets/location.svg') }"
        />

        <!-- Marker: Main Registered Location -->
        <gmap-marker
          v-if="mainMarker"
          :position="mainMarker"
          :icon="{ url: require('../../assets/customer_home_alpha.svg') }"
        />

        <!-- Marker: Compared to Registered Location -->
        <gmap-marker
          v-if="compareMarker"
          :zIndex="100"
          :position="compareMarker"
          :draggable="true"
          :icon="{ url: require('../../assets/customer_home.svg') }"
          @dragstart="didPinStartDrag"
          @dragend="didPinEndDrag"
        >
          <!-- Map Infomation Window -->
          <gmap-info-window
            :opened="infoWinOpen"
            @closeclick="infoWinOpen = false"
          >
            <!-- Infomation Window Detail : Service Available-->
            <b-notification
              v-if="type === 'service' || type === 'prospect'"
              :closable="false"
              :type="isServiceAvailable ? 'is-success' : 'is-danger'"
              style="font-family: Dtac2018, sans-serif; padding: 1.25rem;"
            >
              <div
                v-if="isServiceAvailable"
                class="columns is-multiline is-gapless dtac-font"
              >
                <div class="column has-text-centered is-full">
                  <b-icon icon="check-circle" size="is-large" type="is-light" />
                </div>
                <div
                  class="column has-text-centered is-full"
                  style="margin-top: 1rem;"
                >
                  <p
                    class="title is-5 is-hidden-mobile"
                    style="margin-bottom: 0px;"
                  >
                    {{ $t("maps.service_available") }}
                  </p>
                  <p class="is-hidden-tablet">
                    {{ $t("maps.service_available") }}
                  </p>
                  <p style="margin-top: 0.5rem;">
                    {{ compareMarker.lat | todecimal(8) }},{{
                      compareMarker.lng | todecimal(8)
                    }}
                  </p>

                  <p v-if="pinDistance">
                    {{ (pinDistance * 1000) | todecimal(2) }} m.
                  </p>
                </div>
              </div>
              <!-- Infomation Window Detail : Service Unavailable-->
              <div v-else class="columns is-multiline is-gapless dtac-font">
                <div class="column has-text-centered is-full">
                  <b-icon icon="close-circle" size="is-large" type="is-light" />
                </div>
                <div
                  class="column has-text-centered is-full"
                  style="margin-top: 1rem;"
                >
                  <p
                    class="title is-5 is-hidden-mobile"
                    style="margin-bottom: 0px;"
                  >
                    {{ $t("maps.service_unavailable") }}
                  </p>
                  <p class="is-hidden-tablet">
                    {{ $t("maps.service_unavailable") }}
                  </p>
                  <p style="margin-top: 0.5rem;">
                    {{ compareMarker.lat | todecimal(8) }},{{
                      compareMarker.lng | todecimal(8)
                    }}
                  </p>
                  <p v-if="pinDistance">
                    {{ (pinDistance * 1000) | todecimal(2) }} m.
                  </p>
                </div>
              </div>
            </b-notification>

            <!-- Infomation Window Detail : Site Available-->
            <b-notification
              v-if="type === 'site'"
              :closable="false"
              :type="isSiteAvailable ? 'is-success' : 'is-danger'"
              style="font-family: Dtac2018, sans-serif; padding: 1.25rem;"
            >
              <div
                v-if="isSiteAvailable"
                class="columns is-multiline is-gapless dtac-font"
              >
                <div class="column has-text-centered is-full">
                  <b-icon icon="check-circle" size="is-large" type="is-light" />
                </div>
                <div
                  class="column has-text-centered is-full"
                  style="margin-top: 1rem;"
                >
                  <p
                    class="title is-5 is-hidden-mobile"
                    style="margin-bottom: 0px;"
                  >
                    {{ $t("maps.site_available") }}
                  </p>
                  <p class="is-hidden-tablet">
                    {{ $t("maps.site_available") }}
                  </p>
                  <p style="margin-top: 0.5rem;">
                    {{ compareMarker.lat | todecimal(8) }},{{
                      compareMarker.lng | todecimal(8)
                    }}
                  </p>
                </div>
              </div>
              <!-- Infomation Window Detail : Site Unavailable-->
              <div v-else class="columns is-multiline is-gapless dtac-font">
                <div class="column has-text-centered is-full">
                  <b-icon icon="close-circle" size="is-large" type="is-light" />
                </div>
                <div
                  class="column has-text-centered is-full"
                  style="margin-top: 1rem;"
                >
                  <p
                    class="title is-5 is-hidden-mobile"
                    style="margin-bottom: 0px;"
                  >
                    {{ $t("maps.site_unavailable") }}
                  </p>
                  <p class="is-hidden-tablet">
                    {{ $t("maps.site_unavailable") }}
                  </p>
                  <p style="margin-top: 0.5rem;">
                    {{ compareMarker.lat | todecimal(8) }},{{
                      compareMarker.lng | todecimal(8)
                    }}
                  </p>
                </div>
              </div>
            </b-notification>
          </gmap-info-window>
          <!-- END : Map Infomation Window -->
        </gmap-marker>

        <!-- Circles : Maps Area -->
        <gmap-circle
          v-for="(coverage, index) in coverageCircles"
          :key="index"
          :center="coverage.position"
          :radius="coverage.radius"
          :visible="true"
          :options="coverage.options"
        />
      </gmap-map>
      <!-- END : Service Location Maps -->
    </section>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { gmapApi } from "vue2-google-maps";
import MapApiManager from "@/apis/MapApiManager";
import AlertManager from "@/managers/AlertManager";
import GeoSearch from "@/components/GeoSearch.vue";

export default {
  name: "LocationMap",
  props: ["isForm", "clear", "type"],
  components: {
    GeoSearch
  },
  data() {
    return {
      geoSearchPressed: false,
      selectedPlace: null,
      center: { lat: 13.732447299999999, lng: 100.53140549999999 },
      mainMarker: null,
      compareMarker: null,
      pinDistance: null,
      myLocation: null,
      existingAreas: [],
      coverageCircles: [],
      infoWinOpen: false,
      isFirstLoad: false,
      isEnableMapIdle: false,
      isShowAreasPress: false,
      isMyLocationPress: false,
      isServiceAvailable: false,
      isSiteAvailable: false,
      coordi: null
    };
  },
  computed: {
    ...mapState({
      userProfile: state => state.userProfile
    }),
    google: gmapApi
  },
  watch: {
    clear: function(val) {
      if (val) {
        this.setUpMainMarkers(null);
        this.pinDistance = null;
        this.infoWinOpen = false;
        this.isServiceAvailable = false;
      }
    }
  },
  filters: {
    todecimal: function(value, decimal_place) {
      if (!value) return "";
      return Number(value).toFixed(decimal_place);
    }
  },
  methods: {
    updateSearchError: function(message) {
      this.geoSearchPressed = false;
      AlertManager.showErrorSnackbar(message);
    },
    updateSearchLocationPin: function(coordinates) {
      this.geoSearchPressed = false;
      this.coordi = coordinates;
      this.panCameraToLocation(coordinates);
      this.pinMarkerOnLocation(coordinates);
    },
    /*
     Find the place following the criterias ( seleced, text-input, or lat-lng input )
    */
    searchPlaceFromInput: function() {
      this.geoSearchPressed = true;
    },
    /*
      Convert Input Lat-Lng String (GeoString) to Google Lat-Lng format {lat: number, lng: number}
    */
    convertGeoStringToCoordinate: function(geoString) {
      const noSpaceGeoString = geoString.split(" ").join("");
      const splitGeoString = noSpaceGeoString.split(",");
      const coordinate = {
        lat: Number(splitGeoString[0]),
        lng: Number(splitGeoString[1])
      };
      this.panCameraToLocation(coordinate);
      this.pinMarkerOnLocation(coordinate);
    },
    /*
      Convert Input Address String to Lat-Lng by Google Geocoder
    */
    convertAddressToPlace: function(address) {
      const _this = this;
      const geocoder = new this.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status == this.google.maps.GeocoderStatus.OK) {
          const coordinate = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          };
          _this.panCameraToLocation(coordinate);
          _this.pinMarkerOnLocation(coordinate);
        }
      });
    },
    /*
      Pan Google Map's camera to input coordinate ( lat, lng )
    */
    panCameraToLocation: function(coordinate, zoomLevel) {
      this.$refs.locationmap.$mapPromise.then(map => {
        map.panTo(coordinate);
        if (zoomLevel) {
          map.setZoom(zoomLevel);
        }
      });
    },
    /*
      Get current device coordinate by geolocation in navigator
    */
    getCurrentLocation: function() {
      this.isFirstLoad = true;

      // Check is current device contain "geolocation"
      if ("geolocation" in navigator) {
        /* geolocation is available */
        const myLocationWatchID = navigator.geolocation.watchPosition(
          position => {
            const currentLocation = {
              lat: Number(position.coords.latitude),
              lng: Number(position.coords.longitude)
            };
            this.pinMyLocationMarker(currentLocation);
          }
        );
        this.$emit("update_myLocationWatchID", myLocationWatchID);
      } else {
        /* geolocation IS NOT available */
        alert("geolocation IS NOT available");
      }
    },
    /*
      Pin my-location marker on map
    */
    pinMyLocationMarker: function(coordinate) {
      this.myLocation = coordinate;

      if (this.isFirstLoad || this.isMyLocationPress) {
        this.isFirstLoad = false;
        this.isMyLocationPress = false;
        this.panCameraToLocation(coordinate);
      }
    },
    /*
      Pan Camera to my location without calling get current-location again 
      which will cause multiple geolocation traking id
    */
    myLocationButtonPress: function() {
      this.isMyLocationPress = true;
      this.panCameraToLocation(this.myLocation, 15);

      this.setUpMainMarkers(this.myLocation);
      this.checkServiceAvailability(this.myLocation);
    },
    /*
      Map's Click & Pin's Drag-End handler : Check a service at clicked location on map
    */
    observerGetPinCoordinates: function(event) {
      this.infoWinOpen = false;
      const markerLatLng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      this.pinMarkerOnLocation(markerLatLng);
    },
    /*
      Pin a marker on map following input cordinate
    */
    pinMarkerOnLocation: function(pinnedCoordinate) {
      this.setUpMainMarkers(pinnedCoordinate);
      this.panCameraToLocation(pinnedCoordinate, 15);
      if (pinnedCoordinate && pinnedCoordinate.lat && pinnedCoordinate.lng) {
        if (this.type && this.type === "site") {
          this.checkSiteAvailability(pinnedCoordinate);
        } else {
          this.checkServiceAvailability(pinnedCoordinate);
        }
      }
    },
    showAreaButtonPress: function() {
      this.isShowAreasPress = !this.isShowAreasPress;

      if (this.isShowAreasPress) {
        // Query coverage in bound & draw them on the map
        this.isEnableMapIdle = true;
        this.mapIdleObserver();
      } else {
        // Clear circles
        this.existingAreas = [];
        this.coverageCircles = [];
        this.isEnableMapIdle = false;
      }
    },
    didPinStartDrag: function() {
      this.infoWinOpen = false;
      this.isEnableMapIdle = false;
    },
    didPinEndDrag: function(event) {
      this.infoWinOpen = false;
      this.isEnableMapIdle = true;

      const pinnedCoordinates = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      this.compareMarker = pinnedCoordinates;
      this.checkServiceAvailability(pinnedCoordinates);
      this.calculateDistanceBetweenPins();
    },
    setUpMainMarkers: function(coordinates) {
      this.pinDistance = null;

      this.mainMarker = coordinates;
      this.compareMarker = coordinates;
    },
    calculateDistanceBetweenPins: function() {
      const lat1 = this.mainMarker.lat;
      const lng1 = this.mainMarker.lng;

      const lat2 = this.compareMarker.lat;
      const lng2 = this.compareMarker.lng;

      if (lat1 == lat2 && lng1 == lng2) {
        return 0;
      } else {
        var radlat1 = (Math.PI * lat1) / 180;
        var radlat2 = (Math.PI * lat2) / 180;
        var theta = lng1 - lng2;
        var radtheta = (Math.PI * theta) / 180;
        var dist =
          Math.sin(radlat1) * Math.sin(radlat2) +
          Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
        if (dist > 1) {
          dist = 1;
        }
        dist = Math.acos(dist);
        dist = (dist * 180) / Math.PI;
        dist = dist * 60 * 1.1515;
        this.pinDistance = dist * 1.609344;
      }
    },
    /*
      Map Idel handler : get list of coverage areas inside map's bound
    */
    mapIdleObserver: async function() {
      if (this.isShowAreasPress && this.isEnableMapIdle) {
        const loadingComponent = this.$buefy.loading.open({
          container: null
        });

        this.$refs.locationmap.$mapPromise.then(async map => {
          const bounds = map.getBounds();
          const zoomLevel = map.getZoom();
          if (bounds) {
            const responseCoverages = await MapApiManager.requestCoverageAreas(
              bounds,
              zoomLevel
            );
            loadingComponent.close();

            if (responseCoverages && responseCoverages.length > 0) {
              responseCoverages.forEach(newArea => {
                const previousArea = this.existingAreas.find(
                  coverageInfo => coverageInfo.name === newArea.name
                );

                // If new area contained coverage value & not aleardy display on map, then displayed it
                if (!previousArea) {
                  this.drawCircleOnMap(newArea);
                  this.existingAreas.push(newArea);
                }
              });
            }
          } else {
            alert("bounds not found");
          }
        });
      }
    },
    /*
      Draw Circle on map with different options base on criterias
    */
    drawCircleOnMap: function(area) {
      let circle = {
        position: new this.google.maps.LatLng(area.latitude, area.longitude),
        options: {
          fillOpacity: 0.4,
          strokeWeight: 0
        }
      };

      if (area.utilize > 40) {
        // Red Area: Cannot Sale with utilize >= 50%
        circle.options.fillColor = "#E74C3C";
        circle.options.fillOpacity = 0.2;
      } else if (area.utilize >= 0 && area.utilize <= 40) {
        // Green Area: Can Sale with utilize < 40%
        circle.options.fillColor = "#80D840";
      } else {
        // Red Area: Unknown with no utilize
        circle.options.fillColor = "#E74C3C";
      }

      if (area.coverage) {
        if (area.coverage > 2000) {
          circle.radius = 2000;
        } else {
          circle.radius = Math.abs(area.coverage);
        }
      } else {
        circle.radius = 500;
      }

      this.coverageCircles.push(circle);
    },
    /*
      Check Service Availability for pin or search location
    */
    checkSiteAvailability: async function(coordinate) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const siteAvailabilityInfo = await MapApiManager.requestSiteServiceAvailable(
        coordinate
      );
      loadingComponent.close();
      this.infoWinOpen = true;

      switch (siteAvailabilityInfo.code) {
        case "0":
          this.isSiteAvailable = true;
          this.emitUpdateAreaResult(
            true,
            siteAvailabilityInfo.data.location_token
          );
          break;

        default:
          this.isSiteAvailable = false;
          AlertManager.showBusinessErrorAlertDialog(
            siteAvailabilityInfo.code,
            siteAvailabilityInfo.message
          );
          this.emitUpdateAreaResult(false, null);
          break;
      }
    },
    /*
      Check Service Availability for pin or search location
    */
    checkServiceAvailability: async function(coordinate) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const serviceAvailabilityInfo = await MapApiManager.requestLocationServiceAvailable(
        coordinate
      );
      loadingComponent.close();

      this.infoWinOpen = true;
      // Check Condition: is coordinate availabile for sale
      if (serviceAvailabilityInfo.result) {
        // coordinate availabile for sale
        this.isServiceAvailable = true;
        this.emitUpdateAreaResult(true, serviceAvailabilityInfo.location_token);
      } else {
        // coordinate unavailabile for sale
        this.isServiceAvailable = false;
        this.emitUpdateAreaResult(false, null);
      }
    },
    /*
      Emit an event "go_to_number_register_active" to change active step to 1 ( Number Register Page )
    */
    goToNumberRegisterStep: function() {
      this.$emit("go_to_number_register_active");
    },
    emitUpdateAreaResult: function(service, token) {
      this.$emit("update_service_area_status", {
        service,
        token
      });
    }
  },
  mounted() {
    this.getCurrentLocation();
  }
};
</script>

<style scoped>
.map {
  width: 100%;
  height: 68vh;
}

@media only screen and (max-device-width: 768px) {
  .map {
    width: 100%;
    height: 50vh;
  }
}
.no-padding {
  padding: 1.5rem 0px;
}
</style>
